import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { UserAvatar } from './UserAvatar';
import { UserDetailView } from '@/lib/api/users';
import { Mail, Phone, MapPin, Briefcase, Calendar, Award, TrendingUp, Target, Send, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTenant } from '@/contexts/TenantContext';
import { useOrganisation } from '@/contexts/OrganisationContext';
import { OrganisationAccessSummary } from './OrganisationAccessSummary';

interface UserDetailModalProps {
  user: UserDetailView | null;
  loading: boolean;
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
}

// See note in UserTable.tsx — `admin` has broader access than `company_admin`,
// so admin = Tenant Admin and company_admin = Branch Admin.
const roleConfig: Record<string, { color: string; icon: string; label: string }> = {
  admin: { color: 'bg-green-100 text-green-700', icon: '👑', label: 'Tenant Admin' },
  company_admin: { color: 'bg-purple-100 text-purple-700', icon: '🛡️', label: 'Branch Admin' },
  user: { color: 'bg-blue-100 text-blue-700', icon: '👤', label: 'User' },
  editor: { color: 'bg-amber-100 text-amber-700', icon: '✍️', label: 'Editor' },
  sales_rep: { color: 'bg-cyan-100 text-cyan-700', icon: '💼', label: 'Sales Rep' },
};

export function UserDetailModal({ user, loading, open, onClose, onEdit }: UserDetailModalProps) {
  const [resending, setResending] = useState(false);
  const { isMasterTenant } = useTenant();
  const { hasEnterpriseHierarchy } = useOrganisation();

  const hasActiveOrganisationAccess = Boolean(
    user?.organisation_assignments?.some((assignment) => assignment.status === 'active'),
  );
  const canResendInvite = Boolean(
    user?.email && (hasEnterpriseHierarchy ? hasActiveOrganisationAccess : user.role !== 'admin'),
  );

  const handleResendInvite = async () => {
    if (!user?.email || (!hasEnterpriseHierarchy && !user.company_id)) return;
    setResending(true);
    try {
      const redirectTo = `${window.location.origin}/login`;
      const { data, error } = await supabase.functions.invoke('tenant-invite-user', {
        body: {
          email: user.email,
          display_name: user.display_name,
          company_id: user.company_id || '',
          // Enterprise authority is derived server-side from this exact
          // profile's active assignment. Never send legacy `admin`: the Edge
          // Function correctly rejects clients trying to grant that role.
          role: hasEnterpriseHierarchy ? 'user' : (user.role || 'user'),
          ...(hasEnterpriseHierarchy ? { profile_id: user.id } : {}),
          redirect_to: redirectTo,
        },
      });

      if (error || !data?.success) {
        throw new Error((data as any)?.error || error?.message || 'Failed to resend invite');
      }

      toast.success('✅ Email sent. Ask the user to check inbox/spam and click the link to set their password.');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to resend invite');
    } finally {
      setResending(false);
    }
  };
  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Loading User Details...</DialogTitle>
            <DialogDescription className="sr-only">Loading user profile information</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <Skeleton className="w-24 h-24 rounded-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
            <div className="space-y-4">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">User Details</DialogTitle>
          <DialogDescription className="sr-only">View user profile, activity, and performance details</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left: Profile Info */}
          <div className="space-y-6">
            <div className="flex flex-col items-center text-center space-y-3">
              <UserAvatar avatarUrl={user.avatar_url} displayName={user.display_name} size="lg" />
              <div>
                <h3 className="text-2xl font-bold text-foreground">{user.display_name}</h3>
                {isMasterTenant && <p className="text-sm text-muted-foreground">@{user.username}</p>}
              </div>
              {hasEnterpriseHierarchy ? (
                <Badge
                  variant="outline"
                  className={user.is_active === false
                    ? 'border-slate-300 bg-slate-100 text-slate-600'
                    : user.role_pending
                      ? 'border-dashed border-amber-300 bg-amber-50 text-amber-700'
                      : 'border-emerald-200 bg-emerald-50 text-emerald-800'}
                >
                  {user.is_active === false
                    ? 'Inactive account'
                    : user.role_pending
                      ? 'Pending sign-in'
                      : 'Active account'}
                </Badge>
              ) : (
                <Badge
                  className={`${roleConfig[user.role]?.color || roleConfig.user.color} px-3 py-1`}
                >
                  <span className="mr-1">{roleConfig[user.role]?.icon || '👤'}</span>
                  {roleConfig[user.role]?.label || 'User'}
                </Badge>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span className="text-foreground">{user.email}</span>
              </div>

              {user.phone && (
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <span className="text-foreground">{user.phone}</span>
                </div>
              )}

              {user.location && (
                <div className="flex items-center gap-3 text-sm">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span className="text-foreground">{user.location}</span>
                </div>
              )}

              {user.job_title ? (
                <div className="flex items-center gap-3 text-sm">
                  <Briefcase className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Job title</div>
                    <div className="font-medium text-foreground">{user.job_title}</div>
                  </div>
                </div>
              ) : null}

              {user.company_name ? (
                <div className="flex items-center gap-3 text-sm">
                  <Briefcase className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Branch</div>
                    <div className="font-medium text-foreground">{user.company_name}</div>
                  </div>
                </div>
              ) : null}

              <div className="flex items-center gap-3 text-sm">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-foreground">Joined {user.created_at}</span>
              </div>
            </div>

            {hasEnterpriseHierarchy ? (
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold text-foreground">Organisation access</h4>
                  <p className="text-xs text-muted-foreground">
                    Platform permissions are separate from the person&apos;s job title.
                  </p>
                </div>
                <OrganisationAccessSummary assignments={user.organisation_assignments} />
              </div>
            ) : null}
          </div>

          {/* Right: Performance Stats */}
          <div className="space-y-6">
            {/* Stats Card */}
            <div className="bg-muted rounded-lg p-6 space-y-4">
              <h4 className="font-semibold text-foreground flex items-center gap-2">
                <Award className="w-5 h-5 text-primary" />
                Performance Statistics
              </h4>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-background rounded-lg p-4">
                  <div className="text-2xl font-bold text-primary">{user.total_sessions || 0}</div>
                  <div className="text-xs text-muted-foreground">Total Sessions</div>
                </div>

                <div className="bg-background rounded-lg p-4">
                  <div className="text-2xl font-bold text-primary">{user.credits || 0}</div>
                  <div className="text-xs text-muted-foreground">Credits</div>
                </div>

                {user.average_score !== undefined && (
                  <div className="bg-background rounded-lg p-4">
                    <div className="text-2xl font-bold text-primary flex items-center gap-1">
                      <TrendingUp className="w-5 h-5" />
                      {user.average_score.toFixed(1)}%
                    </div>
                    <div className="text-xs text-muted-foreground">Average Score</div>
                  </div>
                )}

                {user.best_score !== undefined && (
                  <div className="bg-background rounded-lg p-4">
                    <div className="text-2xl font-bold text-primary flex items-center gap-1">
                      <Target className="w-5 h-5" />
                      {user.best_score}%
                    </div>
                    <div className="text-xs text-muted-foreground">Best Score</div>
                  </div>
                )}
              </div>

              {user.last_session_date && (
                <div className="text-xs text-muted-foreground pt-2">
                  Last active: {new Date(user.last_session_date).toLocaleDateString()}
                </div>
              )}
            </div>

            {/* Recent Activity */}
            <div className="bg-muted rounded-lg p-6 space-y-4">
              <h4 className="font-semibold text-foreground">Recent Activity</h4>

              {user.recent_evaluations && user.recent_evaluations.length > 0 ? (
                <div className="space-y-2">
                  {user.recent_evaluations.map((evaluation) => (
                    <div
                      key={evaluation.id}
                      className="bg-background rounded-lg p-3 flex items-center justify-between"
                    >
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {evaluation.agent_name}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(evaluation.evaluated_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div
                        className={`text-lg font-bold ${
                          evaluation.overall_score >= 80
                            ? 'text-green-600'
                            : evaluation.overall_score >= 60
                            ? 'text-yellow-600'
                            : 'text-red-600'
                        }`}
                      >
                        {evaluation.overall_score}%
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground text-center py-4">
                  No recent activity
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>

          {/* Resend Invite (tenant users only) */}
          {canResendInvite && (
            <Button variant="outline" onClick={handleResendInvite} disabled={resending} className="gap-2">
              {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Resend Email
            </Button>
          )}

          <Button onClick={onEdit}>Edit User</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
